import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Send } from 'lucide-react';
import { mapCourierError, useTranslation } from '@dukanti/shared';
import type {
  Courier,
  CourierErrorKey,
  CourierVerificationResult,
  TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateCourierVerification } from '@/hooks/use-create-courier-verification';
import { useResendCourierVerification } from '@/hooks/use-resend-courier-verification';
import { useVerifyCourierOtp } from '@/hooks/use-verify-courier-otp';

export interface CourierVerificationDialogProps {
  readonly open: boolean;
  readonly storeId: string;
  readonly courier: Courier | null;
  readonly onOpenChange: (open: boolean) => void;
}

function formatCooldown(value: string | null, lang: string): string {
  if (!value) return '';
  return new Intl.DateTimeFormat(lang, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export function CourierVerificationDialog({
  open,
  storeId,
  courier,
  onOpenChange,
}: CourierVerificationDialogProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const [otp, setOtp] = useState('');
  const [lastResult, setLastResult] = useState<CourierVerificationResult | null>(null);
  const [debugOtp, setDebugOtp] = useState<string | null>(null);
  const [localError, setLocalError] = useState<CourierErrorKey | null>(null);
  const createVerification = useCreateCourierVerification(storeId);
  const resendVerification = useResendCourierVerification(storeId);
  const verifyOtp = useVerifyCourierOtp(storeId);

  const isSending = createVerification.isPending || resendVerification.isPending;
  const isPending = isSending || verifyOtp.isPending;
  const cooldownText = useMemo(
    () => formatCooldown(lastResult?.resend_available_at ?? null, lang),
    [lang, lastResult?.resend_available_at],
  );
  const isApproved =
    courier?.verification_status === 'approved' ||
    lastResult?.courier?.verification_status === 'approved';
  const canVerify = otp.length === 6 && !!courier && !isApproved && !isPending;
  const localDebugOtp = import.meta.env.DEV ? debugOtp : null;

  useEffect(() => {
    if (!open) {
      setOtp('');
      setLastResult(null);
      setDebugOtp(null);
      setLocalError(null);
    }
  }, [open]);

  function handleResult(result: CourierVerificationResult): void {
    setLastResult(result);
    if (result.debug_otp) setDebugOtp(result.debug_otp);
    setLocalError(result.success ? null : mapCourierError(undefined, result));
  }

  function send(): void {
    if (!courier) return;
    setLocalError(null);
    createVerification.mutate(courier.id, { onSuccess: handleResult });
  }

  function resend(): void {
    if (!courier) return;
    setLocalError(null);
    resendVerification.mutate(courier.id, { onSuccess: handleResult });
  }

  function verify(): void {
    if (!courier || !canVerify) return;
    setLocalError(null);
    verifyOtp.mutate(
      { courierId: courier.id, otp },
      {
        onSuccess: (result) => {
          handleResult(result);
          if (result.success) onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} data-testid="courier-verification-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.couriers.verification.title' as TranslationKey)}</DialogTitle>
          <DialogDescription>
            {courier
              ? t('admin.couriers.verification.sent' as TranslationKey, {
                  phone: courier.phone_number,
                })
              : t('admin.couriers.verification.enterOtp' as TranslationKey)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isApproved ? (
            <div
              data-testid="courier-verification-success"
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-start text-sm text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-200"
            >
              {t('admin.couriers.verification.approved' as TranslationKey)}
            </div>
          ) : null}

          {localError ? (
            <div
              role="alert"
              data-testid="courier-verification-error"
              className="border-destructive/25 bg-destructive/10 text-destructive flex items-start gap-2 rounded-lg border p-3 text-start text-sm"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{t(localError as TranslationKey)}</span>
            </div>
          ) : null}

          {cooldownText && !isApproved ? (
            <p className="text-muted-foreground text-start text-sm">
              {t('admin.couriers.verification.cooldown' as TranslationKey, {
                time: cooldownText,
              })}
            </p>
          ) : null}

          {localDebugOtp ? (
            <div
              className="border-muted-foreground/40 bg-muted/40 rounded-lg border border-dashed p-3 text-start"
              data-testid="courier-verification-debug-otp"
            >
              <p className="text-muted-foreground text-xs">
                {t('admin.couriers.verification.localDebugOtp' as TranslationKey)}
              </p>
              <p className="text-foreground mt-1 font-mono text-lg font-semibold tracking-[0.25em]">
                {localDebugOtp}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                {t('admin.couriers.verification.localDebugOtpHelp' as TranslationKey)}
              </p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="courier-verification-otp">
              {t('admin.couriers.verification.enterOtp' as TranslationKey)}
            </Label>
            <Input
              id="courier-verification-otp"
              data-testid="courier-verification-otp"
              value={otp}
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              maxLength={6}
              pattern="[0-9]*"
              disabled={!courier || isApproved || isPending}
              className="text-center font-mono tracking-[0.35em]"
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') verify();
              }}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!courier || isApproved || isPending}
            onClick={lastResult ? resend : send}
          >
            {isSending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Send className="size-4" aria-hidden />
            )}
            <span>
              {t(
                (lastResult
                  ? 'admin.couriers.verification.resendOtp'
                  : 'admin.couriers.verification.sendOtp') as TranslationKey,
              )}
            </span>
          </Button>
          <Button type="button" disabled={!canVerify} onClick={verify}>
            {verifyOtp.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span>{t('admin.couriers.verification.verify' as TranslationKey)}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
